// src/exitInterviews/index.js — 🚪 exit interviews, the admin tab (v131.1).
// Bundled to dist/exitInterviews.js, loaded by admin.html, rendered into
// #exitInterviewsRoot by switchGroupSub('people','exitinterviews').
//
// ADMIN APP ONLY. exit_interviews is never on /api/sync and this bundle is
// never loaded by index.html - the staff app must not carry HR code at all.
//
// Everything here reads through the Worker, which filters every row through
// D&G's canSeeCase(). This bundle does NO access logic of its own: a second
// implementation of "who may see this" is how the two drift apart, and the day
// they disagree a leaver's answers reach the manager they were about.
//
// Ships DARK until migration v204-0 runs: the list read fails and the tab says
// so, naming the migration, rather than showing an empty state that looks like
// "nobody has left".

// The catalogue is imported, not fetched. It has zero dependencies so that
// the Worker AND the admin app can share ONE list - the BADGES pattern. A
// copy here would drift, and then these labels stop matching the questions
// the leaver actually answered.
import { EXIT_QUESTIONS } from '../worker/exit-questions.js';

let _rows = [];
let _open = null;      // the interview currently expanded
let _busy = false;
let _dark = null;      // the needs-migration message, when that is the problem

function api(path, opts) {
  return fetch(DB.workerUrl() + path, Object.assign({
    cache: 'no-store',
    headers: { Authorization: 'Bearer ' + DB.getToken(), 'Content-Type': 'application/json' },
  }, opts || {}));
}
// Escapes the APOSTROPHE too, not just the double quote. This markup mixes
// both quote styles, and a leaver writing "the manager's rota" is an
// ordinary sentence, not an edge case. tests/escape-apostrophe_test.js pins it.
const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": "&#39;" };
function esc(s) {
  return (typeof escapeHtml === 'function') ? escapeHtml(String(s == null ? '' : s))
    : String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ESCAPES[c]; });
}
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind || 'info'); }
function root() { return document.getElementById('exitInterviewsRoot'); }
function when(iso) { return iso ? String(iso).slice(0, 10) : ''; }

export async function load() {
  _dark = null;
  try {
    const res = await api('/api/admin/exit-interviews');
    const d = await res.json().catch(function () { return null; });
    if (!res.ok || !d || !d.ok) {
      // 503 is the pre-migration state and is EXPECTED - say which migration,
      // because an empty list would read as "nobody has left".
      _dark = (d && d.error) || 'Exit interviews are unavailable.';
      _rows = [];
    } else {
      _rows = d.interviews || [];
    }
  } catch (e) {
    _dark = 'Could not reach the server.';
    _rows = [];
  }
  render();
}

function statusChip(s) {
  const map = { invited: ['var(--warn-deep)', 'var(--warn-bg)', 'Invited'], submitted: ['var(--ok-deep)', 'var(--ok-bg)', 'Answered'], closed: ['var(--text)', 'var(--ap-chip-off)', 'Closed'] };
  const m = map[s] || map.closed;
  return '<span style="background:' + m[1] + ';color:' + m[0] + ';border-radius:999px;padding:2px 9px;font-size:11.5px;font-weight:700">' + m[2] + '</span>';
}

function listHtml() {
  if (_dark) {
    return '<div style="background:var(--warn-bg);border:1px solid var(--ap-amber-line);border-radius:10px;padding:14px;font-size:13.5px;line-height:1.6">' +
      '<strong>Not switched on yet.</strong><br>' + esc(_dark) +
      '<br><span style="color:var(--sub)">Run <code>migrations/v204-0-exit-interviews.sql</code> on this project, then refresh.</span></div>';
  }
  if (!_rows.length) {
    return '<div style="color:var(--sub);font-size:13.5px;padding:10px 2px">' +
      'No exit interviews yet. One is raised automatically when somebody goes on notice, ' +
      'and for anybody deactivated in the last fortnight.</div>';
  }
  let h = '<table style="width:100%;border-collapse:collapse;font-size:13.5px">' +
    '<tr style="text-align:start;color:var(--sub)"><th style="padding:6px 4px">Who</th><th>Store</th><th>Raised</th><th>Status</th><th></th></tr>';
  for (const r of _rows) {
    h += '<tr style="border-top:1px solid var(--border)">' +
      '<td style="padding:8px 4px"><strong translate="no">' + esc(r.staff_name) + '</strong>' +
        (r.follow_up_required ? ' <span title="Follow-up flagged">\u{1F6A9}</span>' : '') + '</td>' +
      '<td translate="no">' + esc(r.store || '') + '</td>' +
      '<td>' + esc(when(r.created_at)) + (r.raised_by === 'deactivated' ? ' <span style="color:var(--sub)">(left)</span>' : '') + '</td>' +
      '<td>' + statusChip(r.status) + '</td>' +
      '<td style="text-align:end"><button onclick="ExitInterviews.open(' + r.id + ')" ' +
        'style="background:none;border:1px solid var(--border);border-radius:8px;padding:5px 10px;cursor:pointer;font-size:12.5px">Open</button></td>' +
      '</tr>';
  }
  return h + '</table>';
}

function answersHtml(row) {
  const qs = (row && row._questions) || [];
  const a = (row && row.answers) || {};
  if (!qs.length) return '';
  let h = '';
  for (const q of qs) {
    const v = a[q.key];
    if (v === undefined || v === null || v === '') continue;
    h += '<div style="margin-bottom:10px"><div style="font-size:12px;color:var(--sub)">' + esc(q.label) + '</div>' +
      '<div style="font-weight:600"' + (q.type === 'text' ? ' translate="no"' : '') + '>' + (q.type === 'scale' ? esc(v) + ' / 5' : esc(v)) + '</div></div>';
  }
  return h || '<div style="color:var(--sub)">Nothing was answered.</div>';
}

function summaryHtml(row) {
  const s = row && row.ai_summary;
  if (!s) {
    return '<button onclick="ExitInterviews.summarise(' + row.id + ')" ' +
      (row.status === 'invited' ? 'disabled title="Not answered yet" ' : '') +
      'style="background:var(--brand);color:#fff;border:none;border-radius:9px;padding:8px 13px;font-size:13px;font-weight:700;cursor:pointer">' +
      '\u2728 Summarise with AI</button>' +
      '<div style="font-size:11.5px;color:var(--sub);margin-top:6px">The answers are sent to Anthropic. It advises; people decide.</div>';
  }
  let h = '<div style="background:var(--surface-2);border:1px solid var(--border);border-radius:10px;padding:12px">' +
    '<div translate="no" style="font-weight:700;margin-bottom:6px">' + esc(s.headline) + '</div>';
  if (Array.isArray(s.actions) && s.actions.length) {
    h += '<div style="font-size:12px;color:var(--sub);margin:8px 0 4px">Suggested actions - these are suggestions, not decisions</div><ul style="margin:0;padding-inline-start:18px">';
    for (const act of s.actions) h += '<li translate="no" style="margin-bottom:4px">' + esc(act.what) + ' <span style="color:var(--sub)">- ' + esc(act.why) + '</span></li>';
    h += '</ul>';
  }
  if (Array.isArray(s.watch) && s.watch.length) {
    h += '<div style="font-size:12px;color:var(--sub);margin:8px 0 4px">Worth watching</div><ul style="margin:0;padding-inline-start:18px">';
    for (const w of s.watch) h += '<li translate="no">' + esc(w) + '</li>';
    h += '</ul>';
  }
  return h + '<div translate="no" style="font-size:11px;color:var(--sub);margin-top:8px">' + esc(s.model || '') + ' \u00B7 ' + esc(when(s.at)) + '</div></div>';
}

function detailHtml() {
  const r = _open;
  if (!r) return '';
  return '<div style="border:1px solid var(--border);border-radius:12px;padding:16px;margin-bottom:14px">' +
    '<div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">' +
      '<div><div translate="no" style="font-size:16px;font-weight:800">' + esc(r.staff_name) + '</div>' +
      '<div style="font-size:12px;color:var(--sub)"><span translate="no">' + esc(r.store || '') + '</span> \u00B7 raised ' + esc(when(r.created_at)) +
      (r.submitted_at ? ' \u00B7 answered ' + esc(when(r.submitted_at)) : '') + '</div></div>' +
      '<button onclick="ExitInterviews.close()" style="margin-inline-start:auto;background:none;border:1px solid var(--border);border-radius:8px;padding:6px 11px;cursor:pointer">Back</button>' +
    '</div>' +
    (r.status === 'invited'
      ? '<div style="background:var(--warn-bg);border:1px solid var(--ap-amber-line);border-radius:10px;padding:12px;font-size:13px;line-height:1.6">' +
        'Not answered yet.' +
        (r._link ? '' : '<div style="margin-top:8px"><button onclick="ExitInterviews.getLink()" ' +
          'style="background:none;border:1px solid var(--border);border-radius:8px;padding:6px 11px;cursor:pointer;font-size:12.5px">' +
          'Get the invite link</button></div>') +
        (r._token ? '<div style="margin-top:8px"><code style="font-size:11.5px;word-break:break-all">' + esc(r._link) + '</code>' +
          '<button onclick="ExitInterviews.copyLink()" style="margin-inline-start:8px;background:none;border:1px solid var(--border);border-radius:8px;padding:4px 9px;cursor:pointer;font-size:12px">Copy</button></div>' : '') +
        '</div>'
      : '<div style="display:grid;grid-template-columns:1fr 1fr;gap:18px">' +
          '<div>' + answersHtml(r) + '</div><div>' + summaryHtml(r) + '</div></div>') +
    '<div style="margin-top:14px;border-top:1px solid var(--border);padding-top:12px">' +
      '<label style="font-size:13px;display:flex;align-items:center;gap:8px;cursor:pointer">' +
        '<input type="checkbox" ' + (r.follow_up_required ? 'checked' : '') + ' onchange="ExitInterviews.setFollowUp(this.checked)"> ' +
        'Needs an Ops Manager follow-up</label>' +
      '<textarea id="eiNote" placeholder="Add a note to the record..." style="width:100%;margin-top:9px;min-height:60px;padding:9px;border:1px solid var(--border);border-radius:9px;font:inherit"></textarea>' +
      '<div style="display:flex;gap:8px;margin-top:8px">' +
        '<button onclick="ExitInterviews.addNote()" style="background:none;border:1px solid var(--border);border-radius:8px;padding:7px 12px;cursor:pointer;font-size:13px">Save note</button>' +
        (r.status !== 'closed' ? '<button onclick="ExitInterviews.closeCase()" style="margin-inline-start:auto;background:none;border:1px solid var(--border);border-radius:8px;padding:7px 12px;cursor:pointer;font-size:13px">Close this interview</button>' : '') +
      '</div>' +
      notesHtml(r) +
    '</div></div>';
}

function notesHtml(r) {
  const notes = Array.isArray(r.follow_up_notes) ? r.follow_up_notes : [];
  if (!notes.length) return '';
  let h = '<div style="margin-top:12px;font-size:12.5px">';
  for (const n of notes) {
    h += '<div translate="no" style="border-top:1px solid var(--border);padding:7px 0">' +
      '<span style="color:var(--sub)">' + esc(when(n.at)) + ' \u00B7 ' + esc(n.by) + '</span><br>' + esc(n.text) + '</div>';
  }
  return h + '</div>';
}

export function render() {
  const el = root();
  if (!el) return;
  el.innerHTML =
    '<div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">' +
      '<div><div style="font-size:16px;font-weight:800">\u{1F6AA} Exit interviews</div>' +
      '<div style="font-size:11.5px;color:var(--sub)">Head Office only \u00B7 their own area manager cannot see these \u00B7 kept 365 days</div></div>' +
      '<button onclick="ExitInterviews.load()" style="margin-inline-start:auto;background:none;border:1px solid var(--border);border-radius:8px;padding:7px 12px;cursor:pointer;font-size:13px">Refresh</button>' +
      '<button onclick="ExitInterviews.raise()" style="background:none;border:1px solid var(--border);border-radius:8px;padding:7px 12px;cursor:pointer;font-size:13px">+ Raise one</button>' +
      '<button onclick="ExitInterviews.aggregate()" style="background:var(--brand);color:#fff;border:none;border-radius:9px;padding:8px 13px;font-size:13px;font-weight:700;cursor:pointer">\u2728 What are leavers telling us?</button>' +
    '</div>' +
    '<div id="eiAggregate"></div>' +
    (_open ? detailHtml() : listHtml());
}

export async function open(id) {
  const res = await api('/api/admin/exit-interviews/' + id);
  const d = await res.json().catch(function () { return null; });
  if (!res.ok || !d || !d.ok) return toast((d && d.error) || 'Could not open that interview', 'error');
  _open = d.interview;
  _open._questions = EXIT_QUESTIONS;
  render();
}

export function close() { _open = null; render(); }

export async function summarise(id) {
  if (_busy) return;
  _busy = true;
  toast('Asking the AI...', 'info');
  try {
    const res = await api('/api/admin/exit-interviews/' + id + '/summarise', { method: 'POST' });
    const d = await res.json().catch(function () { return null; });
    if (!res.ok || !d || !d.ok) return toast((d && d.error) || 'The summary failed', 'error');
    if (_open) { _open.ai_summary = d.summary; render(); }
  } finally { _busy = false; }
}

export async function setFollowUp(on) {
  if (!_open) return;
  const res = await api('/api/admin/exit-interviews/' + _open.id, { method: 'PATCH', body: JSON.stringify({ follow_up_required: !!on }) });
  if (!res.ok) return toast('Could not save that', 'error');
  _open.follow_up_required = !!on;
  const row = _rows.find(function (r) { return r.id === _open.id; });
  if (row) row.follow_up_required = !!on;
  toast(on ? 'Flagged for follow-up' : 'Follow-up flag removed', 'success');
}

export async function addNote() {
  const box = document.getElementById('eiNote');
  const text = box && box.value.trim();
  if (!text || !_open) return;
  const res = await api('/api/admin/exit-interviews/' + _open.id, { method: 'PATCH', body: JSON.stringify({ note: text }) });
  if (!res.ok) return toast('Could not save the note', 'error');
  box.value = '';
  await open(_open.id);
  toast('Note saved', 'success');
}

export async function closeCase() {
  if (!_open) return;
  // Closing is not deleting - the record stays until the 365-day purge. Say so,
  // because "close" reads like "remove" to somebody clicking it in a hurry.
  if (!confirm('Close this interview? It stays on file and readable - closing just takes it off the active list.')) return;
  const res = await api('/api/admin/exit-interviews/' + _open.id, { method: 'PATCH', body: JSON.stringify({ status: 'closed' }) });
  if (!res.ok) return toast('Could not close it', 'error');
  _open = null;
  await load();
  toast('Closed', 'success');
}

export function copyLink() {
  if (!_open || !_open._link) return;
  if (navigator.clipboard) navigator.clipboard.writeText(_open._link).then(function () { toast('Link copied', 'success'); });
  else toast(_open._link, 'info');
}

export async function aggregate() {
  const box = document.getElementById('eiAggregate');
  if (!box || _busy) return;
  _busy = true;
  box.innerHTML = '<div style="color:var(--sub);font-size:13px;padding:8px 2px">Reading them together...</div>';
  try {
    const res = await api('/api/admin/exit-interviews/aggregate', { method: 'POST' });
    const d = await res.json().catch(function () { return null; });
    if (!res.ok || !d || !d.ok) { box.innerHTML = ''; return toast((d && d.error) || 'The overview failed', 'error'); }
    if (d.refused) {
      // Not an error, and not an empty result either - say WHY, or a blank
      // panel reads as "nothing to report".
      box.innerHTML = '<div style="background:var(--surface-2);border:1px solid var(--border);border-radius:10px;padding:12px;font-size:13px;margin-bottom:14px">' +
        esc(d.reason) + '</div>';
      return;
    }
    box.innerHTML = aggregateHtml(d);
  } finally { _busy = false; }
}

function aggregateHtml(d) {
  const a = d.analysis || {};
  const s = d.stats || {};
  let h = '<div style="background:var(--surface-2);border:1px solid var(--border);border-radius:12px;padding:14px;margin-bottom:14px">' +
    '<div translate="no" style="font-weight:800;margin-bottom:6px">' + esc(a.headline) + '</div>' +
    // v135.2: the count, the label and the question each in their own node so each one translates
    '<div style="font-size:12px;color:var(--sub);margin-bottom:10px"><span>' + esc(s.interviews) + ' interviews</span>' +
    (s.lowest ? ' \u00B7 <span>lowest scoring:</span> <span>' + esc(s.lowest.label) + '</span> (' + esc(s.lowest.average) + '/5)' : '') + '</div>';
  if (Array.isArray(a.themes) && a.themes.length) {
    h += '<ul style="margin:0 0 10px;padding-inline-start:18px;font-size:13.5px">';
    for (const t of a.themes) h += '<li style="margin-bottom:5px"><strong translate="no">' + esc(t.theme) + '</strong> <span style="color:var(--sub)">seen in ' + esc(t.seen_in) + '</span></li>';
    h += '</ul>';
  }
  if (Array.isArray(a.actions) && a.actions.length) {
    h += '<div style="font-size:12px;color:var(--sub);margin-bottom:4px">Suggested actions - suggestions, not decisions</div><ul style="margin:0;padding-inline-start:18px;font-size:13.5px">';
    for (const act of a.actions) h += '<li translate="no" style="margin-bottom:4px">' + esc(act.what) + '</li>';
    h += '</ul>';
  }
  if (Array.isArray(a.caveats) && a.caveats.length) {
    h += '<div translate="no" style="font-size:12px;color:var(--sub);margin-top:9px">' + esc(a.caveats.join(' ')) + '</div>';
  }
  return h + '</div>';
}

// Raise one by hand. The nightly sweep does this automatically, but waiting
// until 3am to find out whether the feature works is not a test - and a real
// leaver should not be the first person through the flow.
// v132.49 — the picker. The ops manager, 22 Sep 2026: "which employee number should
// i put to raise the exit interview and get the link. Shift code or EMPXXXXX
// both not working".
//
// He could not have got it right. The old prompt asked for the internal
// database id, a number THE ADMIN APP NEVER SHOWS ANYWHERE, and then told him
// "Admin > People > Staff shows it", which it does not. He tried the employee
// code, the code without its prefix, and finally the clock-in PIN. Asking a
// person to type a primary key is the bug; validating it better would only
// have made the dead end politer.
//
// So: type a few letters of a name, see real people, click one. The list is
// the staff directory the admin app already holds, filtered to this store
// group, so no new endpoint and no extra read.
let _staff = null;   // fetched once per tab visit, only if the global is empty

function staffList() {
  if (Array.isArray(_staff) && _staff.length) return _staff;
  const rows = (typeof staffData !== 'undefined' && Array.isArray(staffData)) ? staffData : [];
  return rows.filter((s) => s && s.id != null && s.name);
}

// staffData is the People tab's list, so it is empty for anyone who opens Exit
// interviews first. Telling them to go and visit another tab would be a worse
// version of the same fault this fixes, so fetch it. Secrets are stripped
// server side (stripStaffSecrets); this never sees a PIN.
async function ensureStaff() {
  if (staffList().length) return;
  try {
    const res = await api('/admin/staff');
    const rows = await res.json().catch(() => null);
    if (Array.isArray(rows)) _staff = rows.filter((s) => s && s.id != null && s.name);
  } catch (e) { /* the picker still shows its own empty-state message */ }
}

function pickerHtml(q) {
  const term = String(q || '').trim().toLowerCase();
  const all = staffList();
  const hits = (term
    ? all.filter((s) => String(s.name).toLowerCase().indexOf(term) !== -1 ||
                        String(s.emp_code || '').toLowerCase().indexOf(term) !== -1)
    : all
  ).slice(0, 40);
  const rows = hits.map((s) =>
    '<button type="button" class="ei-pick" data-id="' + esc(s.id) + '" data-name="' + esc(s.name) + '" ' +
    'style="display:block;width:100%;text-align:start;padding:9px 12px;border:0;border-bottom:1px solid var(--border);background:var(--card);cursor:pointer;font:inherit;font-size:13.5px">' +
      '<b translate="no">' + esc(s.name) + '</b>' +
      '<span translate="no" style="color:var(--sub);font-size:12px"> &middot; ' + esc(s.store || 'no store') +
      (s.emp_code ? ' &middot; ' + esc(s.emp_code) : '') + '</span>' +
    '</button>').join('');
  return rows || '<div style="padding:14px;color:var(--sub);font-size:13px">' +
    (all.length ? 'Nobody matches that. Try part of their name, or their employee code.'
                : 'The staff list has not loaded yet. Open People &gt; Directory once, then try again.') +
    '</div>';
}

export async function raise() {
  const host = root();
  if (!host) return;
  await ensureStaff();
  let box = document.getElementById('eiPicker');
  if (box) box.remove();
  box = document.createElement('div');
  box.id = 'eiPicker';
  box.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:60px 16px';
  box.innerHTML =
    '<div style="background:var(--card);border-radius:14px;max-width:460px;width:100%;overflow:hidden;box-shadow:0 18px 50px rgba(0,0,0,.28)">' +
      '<div style="padding:14px 16px;border-bottom:1px solid var(--border)">' +
        '<div style="font-weight:700;font-size:15px">Who is leaving?</div>' +
        '<div style="font-size:12px;color:var(--sub);margin-top:2px">Search by name or employee code, then pick them from the list.</div>' +
        '<input id="eiPickerQ" placeholder="Start typing a name..." autocomplete="off" ' +
          'style="margin-top:10px;width:100%;box-sizing:border-box;font:inherit;font-size:14px;padding:9px 11px;border:1px solid var(--border-strong);border-radius:9px">' +
      '</div>' +
      '<div id="eiPickerList" style="max-height:46vh;overflow:auto">' + pickerHtml('') + '</div>' +
      '<div style="padding:10px 16px;border-top:1px solid var(--border);text-align:end">' +
        '<button type="button" id="eiPickerCancel" style="font:inherit;font-size:13px;padding:7px 14px;border:1px solid var(--border-strong);background:var(--card);border-radius:9px;cursor:pointer">Cancel</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(box);

  const shut = () => { const b = document.getElementById('eiPicker'); if (b) b.remove(); };
  box.addEventListener('click', (ev) => { if (ev.target === box) shut(); });
  document.getElementById('eiPickerCancel').addEventListener('click', shut);
  const q = document.getElementById('eiPickerQ');
  q.addEventListener('input', () => {
    document.getElementById('eiPickerList').innerHTML = pickerHtml(q.value);
  });
  document.getElementById('eiPickerList').addEventListener('click', (ev) => {
    const btn = ev.target.closest && ev.target.closest('.ei-pick');
    if (!btn) return;
    shut();
    raiseFor(btn.getAttribute('data-id'), btn.getAttribute('data-name'));
  });
  q.focus();
}

async function raiseFor(id, name) {
  if (!id) return;
  if (!confirm('Raise an exit interview for ' + name + '?\n\nThis creates a one-time invite link to send them.')) return;
  const res = await api('/api/admin/exit-interviews', { method: 'POST', body: JSON.stringify({ staff_id: Number(id) }) });
  const d = await res.json().catch(function () { return null; });
  if (!res.ok || !d || !d.ok) return toast((d && d.error) || 'Could not raise it', 'error');
  // The token comes back exactly ONCE - this is the only moment the link can
  // be captured, so show it rather than quietly storing it somewhere.
  const link = location.origin + '/exit-interview.html?token=' + d.token;
  await load();
  await open(d.id);
  if (_open) { _open._token = d.token; _open._link = link; render(); }
  toast('Raised - copy the link and send it to them', 'success');
}
// Fetch the invite link for an interview that is still open.
//
// The nightly sweep raises most interviews and discards the token, so without
// this the swept ones could never be sent to anybody - the record would sit at
// Invited forever while the person was never actually invited. Head Office
// only, and the Worker writes an audit row: handing out a credential should
// leave a trace.
export async function getLink() {
  if (!_open) return;
  const res = await api('/api/admin/exit-interviews/' + _open.id + '/link', { method: 'POST' });
  const d = await res.json().catch(function () { return null; });
  if (!res.ok || !d || !d.ok) return toast((d && d.error) || 'Could not get the link', 'error');
  _open._token = d.token;
  _open._link = location.origin + '/exit-interview.html?token=' + d.token;
  render();
  toast('Link ready - copy it and send it to them', 'success');
}